import { CircleDollarSign, FileSearch, Scale, HandCoins } from "lucide-react";
import LeadForm from "./LeadForm";

const STEPS = [
  {
    icon: FileSearch,
    t: "We search public records",
    d: "Our team checks auction results, court filings, and trustee reports in your county — at no cost to you.",
  },
  {
    icon: Scale,
    t: "We file the claim",
    d: "Surplus funds claims involve strict deadlines and paperwork. We handle everything from affidavits to court filings.",
  },
  {
    icon: HandCoins,
    t: "You get your money",
    d: "When the claim is approved, funds are released directly to you. We only get paid if you do.",
  },
];

export default function SurplusFunds() {
  return (
    <section
      id="surplus-funds"
      data-testid="surplus-funds-section"
      className="py-20 md:py-32 bg-[#0B1B3D] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
        <img
          src="https://images.pexels.com/photos/36729962/pexels-photo-36729962.jpeg"
          alt=""
          className="h-full w-full object-cover"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="mb-4">
            <span className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.24em] text-[#C5A059] font-medium">
              Surplus Funds Recovery
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
            If your home was foreclosed and sold, <em className="text-[#C5A059] not-italic font-medium">you may be owed thousands.</em>
          </h2>
          <p className="text-white/75 leading-relaxed mb-4">
            When a home is sold at foreclosure auction for more than the outstanding
            mortgage, the leftover amount — called <strong className="text-white">surplus funds</strong>
            {" "}or <strong className="text-white">excess proceeds</strong> — legally belongs to the
            former homeowner, not the lender.
          </p>
          <p className="text-white/75 leading-relaxed mb-10">
            But most homeowners are never notified. The money sits in a court
            registry for months, then gets absorbed by the state. We make sure
            that doesn't happen to you.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {STEPS.map((s, i) => (
              <div
                key={i}
                data-testid={`surplus-step-${i}`}
                className="rounded-lg border border-white/15 bg-white/[0.04] backdrop-blur-sm p-6"
              >
                <div className="h-10 w-10 rounded-md bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center mb-4">
                  <s.icon className="h-5 w-5 text-[#C5A059]" />
                </div>
                <div className="font-display text-xl mb-2">{s.t}</div>
                <p className="text-sm text-white/65 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-4 rounded-lg border border-[#C5A059]/30 bg-[#C5A059]/10 p-5">
            <CircleDollarSign className="h-8 w-8 text-[#C5A059] shrink-0" />
            <div>
              <div className="font-medium">No upfront fees — ever.</div>
              <div className="text-sm text-white/70">
                We only earn when you recover your funds. If there's nothing to recover, you owe nothing.
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <LeadForm
            testid="surplus-lead-form"
            compact
            defaultSituation="post_foreclosure_surplus"
          />
        </div>
      </div>
    </section>
  );
}
